import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-accent-500">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 whitespace-nowrap">
              CSSSpreadsheet
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line mr-1.5"></i>
              Home
            </a>

            {/* Categories Dropdown with Images */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 max-h-[500px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer group"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {/* Thumbnail image */}
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-200 border border-background-200/70">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors whitespace-nowrap block">
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap block">
                          Browse on kaspreadsheet
                        </span>
                      </div>
                      <i className="ri-arrow-right-up-line text-foreground-300 group-hover:text-foreground-500 text-sm flex-shrink-0"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/cssbuy-shopping-guide"
              onClick={(e) => {
                e.preventDefault();
                navigate('/cssbuy-shopping-guide');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line mr-1.5"></i>
              Shopping Guide
            </a>

            <a
              href="/cssbuy-spreadsheet-guide"
              onClick={(e) => {
                e.preventDefault();
                navigate('/cssbuy-spreadsheet-guide');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              QC Guide
            </a>
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`text-foreground-800 text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu — Enhanced with category images */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-4 space-y-2 max-h-[70vh] overflow-y-auto">
            {/* Home */}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-home-line text-accent-600"></i>
              </div>
              Home
            </a>

            {/* Shopping Guide */}
            <a
              href="/cssbuy-shopping-guide"
              onClick={(e) => {
                e.preventDefault();
                navigate('/cssbuy-shopping-guide');
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-shopping-bag-line text-accent-600"></i>
              </div>
              Shopping Guide
            </a>

            {/* QC Guide */}
            <a
              href="/cssbuy-spreadsheet-guide"
              onClick={(e) => {
                e.preventDefault();
                navigate('/cssbuy-spreadsheet-guide');
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-guide-line text-accent-600"></i>
              </div>
              QC Guide
            </a>

            {/* Categories — expandable with images */}
            <div>
              <button
                onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-apps-line text-accent-600"></i>
                  </div>
                  Categories
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatExpanded && (
                <div className="mt-1 space-y-0.5 pl-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200 border border-background-200/70">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <span className="font-medium whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mt-3 px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}